/**
 * 档案服务
 * 管理家族档案和人物档案的创建、存储、查询
 */

import type { GameDate } from '../types/game'
import type { Family } from '../types/family'
import type {
  CharacterArchive,
  CharacterIdentity,
  CharacterSchool,
  CharacterAbilities,
  CharacterPersonality,
} from '../types/characterArchive'
import {
  getFactionDefaultSurname,
  getFactionDefaultClan,
  generateSurnameTitle,
  FAMOUS_CLANS,
} from '../data/surnameSystem'
import { getRulerName, getRulerSurname } from '../data/royalFamilyArchives'

// ============================================
// ID 生成器
// ============================================

let familyIdCounter = 0
let characterIdCounter = 0

function generateFamilyId(): string {
  return `family_${Date.now()}_${++familyIdCounter}`
}

function generateCharacterId(): string {
  return `char_${Date.now()}_${++characterIdCounter}`
}

// ============================================
// 家族档案服务
// ============================================

/**
 * 创建家族档案
 */
export function createFamilyArchive(
  factionId: string,
  surname: string,
  clanName: string | undefined,
  rank: Family['rank'],
  type: Family['type'],
  currentDate: GameDate,
  isRulingFamily: boolean = false
): Family {
  const familyId = generateFamilyId()
  const fullName = clanName ? `${surname}${clanName}` : `${surname}氏`

  return {
    id: familyId,
    name: fullName,
    surname,
    clanName,
    fullName: generateSurnameTitle(surname, clanName),
    type,
    rank,
    foundedDate: currentDate,
    factionId,
    isRulingFamily,
    headId: '',
    memberIds: [],
    members: [],
    domain: {
      homeCity: '',
      controlledCities: [],
      influenceCities: [],
      estates: [],
    },
    prestige: rank === 'royal' ? 5000 : rank === 'noble' ? 3000 : 1000,
    reputation: {
      loyalty: 50,
      influence: rank === 'royal' ? 80 : rank === 'noble' ? 60 : 30,
      fear: 30,
      respect: 50,
    },
    traits: [],
    history: [
      {
        id: `event_${Date.now()}`,
        date: currentDate,
        type: 'founded',
        title: '家族建立',
        description: `${fullName}在此时期建立`,
        relatedCharacterIds: [],
        impact: 10,
      },
    ],
    allies: [],
    rivals: [],
    vassals: [],
    wealth: rank === 'royal' ? 10000 : rank === 'noble' ? 5000 : 1000,
    militaryStrength: rank === 'royal' ? 100 : rank === 'noble' ? 60 : 20,
    culturalInfluence: rank === 'royal' ? 100 : rank === 'noble' ? 50 : 20,
    status: 'active',
  }
}

/**
 * 为势力创建初始家族
 */
export function createFactionFamilies(
  factionId: string,
  _scenarioId: string,
  currentDate: GameDate
): Family[] {
  const families: Family[] = []
  const surname = getFactionDefaultSurname(factionId)
  const clanName = getFactionDefaultClan(factionId)

  // 创建统治家族
  const rulingFamily = createFamilyArchive(
    factionId,
    surname,
    clanName,
    'royal',
    'imperial',
    currentDate,
    true
  )
  families.push(rulingFamily)

  // 根据势力大小创建额外的贵族家族
  const additionalFamilyCount = Math.floor(Math.random() * 3) + 2 // 2-4个额外家族

  for (let i = 0; i < additionalFamilyCount; i++) {
    const isSameSurname = Math.random() > 0.5
    const familySurname = isSameSurname ? surname : getRandomSurname()
    const familyClan = isSameSurname ? undefined : getRandomClan(familySurname)

    const family = createFamilyArchive(
      factionId,
      familySurname,
      familyClan,
      'noble',
      'ducal',
      currentDate,
      false
    )
    families.push(family)
  }

  return families
}

// ============================================
// 人物档案服务
// ============================================

/**
 * 创建人物档案
 */
export function createCharacterArchive(
  name: string,
  surname: string,
  clanName: string | undefined,
  identity: CharacterIdentity,
  factionId: string,
  familyId: string,
  birthDate: GameDate,
  gender: 'male' | 'female',
  isRuler: boolean = false
): CharacterArchive {
  const characterId = generateCharacterId()
  const age = calculateAge(birthDate, { year: -770, month: 1, day: 1 })

  // 根据身份生成能力值
  const abilities = generateAbilities(identity, isRuler)
  const personality = generatePersonality()
  const school = generateSchool(identity)

  return {
    id: characterId,
    name,
    surname,
    clanName,
    identity,
    factionId,
    familyId,
    birthDate,
    birthPlace: '',
    isAlive: true,
    gender,
    appearance: {
      description: generateAppearanceDescription(gender, age),
      attractiveness: randomInt(30, 90),
      distinctiveFeatures: [],
    },
    abilities,
    personality,
    school,
    health: {
      currentHealth: 100,
      maxHealth: 100,
      conditions: [],
    },
    fatherId: undefined,
    motherId: undefined,
    spouseIds: [],
    childrenIds: [],
    siblingIds: [],
    relations: [],
    experiences: [
      {
        id: `exp_${Date.now()}`,
        date: birthDate,
        type: 'birth',
        title: '出生',
        description: `${name}出生于${surname}氏家族`,
        location: '',
        relatedCharacterIds: [],
        effects: [],
      },
    ],
    titles: isRuler ? ['君主'] : [],
    positions: [],
    reputation: {
      honor: 50,
      fear: isRuler ? 50 : 20,
      respect: isRuler ? 60 : 30,
      love: isRuler ? 40 : 30,
    },
    wealth: isRuler ? 5000 : identity === 'official' ? 1000 : 100,
    influence: isRuler ? 100 : identity === 'official' ? 50 : 20,
    popularity: isRuler ? 60 : 30,
    traits: [],
    talents: [],
    skills: [],
    status: 'active',
    location: '',
    notes: [],
  }
}

/**
 * 为势力创建初始人物档案
 */
export function createFactionCharacterArchives(
  factionId: string,
  scenarioId: string,
  families: Family[],
  currentDate: GameDate
): CharacterArchive[] {
  const characters: CharacterArchive[] = []
  const rulerName = getRulerName(scenarioId, factionId) || '君主'
  const surname = getRulerSurname(scenarioId, factionId) || '姬'

  // 找到统治家族
  const rulingFamily = families.find(f => f.isRulingFamily)
  if (!rulingFamily) return characters

  // 创建君主
  const ruler = createCharacterArchive(
    rulerName,
    surname,
    rulingFamily.clanName,
    'ruler',
    factionId,
    rulingFamily.id,
    { year: currentDate.year - 35, month: 1, day: 1 },
    'male',
    true
  )
  ruler.titles = ['君主', '国君']
  ruler.positions = [
    {
      id: `pos_${Date.now()}`,
      title: '国君',
      department: '国家',
      level: 10,
      startDate: currentDate,
      factionId,
      responsibilities: ['统治国家', '决策大事', '祭祀祖先'],
      privileges: ['最高权力', '生杀予夺'],
    },
  ]
  ruler.currentPosition = ruler.positions[0]
  characters.push(ruler)

  // 更新家族家主
  rulingFamily.headId = ruler.id
  rulingFamily.memberIds.push(ruler.id)
  rulingFamily.members.push({
    characterId: ruler.id,
    name: rulerName,
    relation: '家主',
    role: 'head',
    joinDate: currentDate,
    isAlive: true,
  })

  // 创建王室成员（君主亲属）
  const royalCount = Math.floor(Math.random() * 3) + 2
  for (let i = 0; i < royalCount; i++) {
    const isMale = Math.random() > 0.4
    const age = randomInt(15, 50)
    const royal = createCharacterArchive(
      generateName(surname, rulingFamily.clanName),
      surname,
      rulingFamily.clanName,
      'royal',
      factionId,
      rulingFamily.id,
      { year: currentDate.year - age, month: randomInt(1, 12), day: randomInt(1, 28) },
      isMale ? 'male' : 'female'
    )
    characters.push(royal)
    rulingFamily.memberIds.push(royal.id)
  }

  // 为每个家族创建成员
  families.forEach(family => {
    if (family.isRulingFamily) return // 统治家族已处理

    const memberCount = Math.floor(Math.random() * 3) + 1
    for (let i = 0; i < memberCount; i++) {
      const isMale = Math.random() > 0.3
      const age = randomInt(20, 60)
      const identity: CharacterIdentity = Math.random() > 0.5 ? 'official' : 'general'
      const character = createCharacterArchive(
        generateName(family.surname, family.clanName),
        family.surname,
        family.clanName,
        identity,
        factionId,
        family.id,
        { year: currentDate.year - age, month: randomInt(1, 12), day: randomInt(1, 28) },
        isMale ? 'male' : 'female'
      )
      characters.push(character)
      family.memberIds.push(character.id)
    }
  })

  // 创建平民百姓
  const commonerCount = Math.floor(Math.random() * 5) + 3
  for (let i = 0; i < commonerCount; i++) {
    const isMale = Math.random() > 0.4
    const age = randomInt(18, 70)
    const commonSurname = getRandomSurname()
    const commoner = createCharacterArchive(
      generateName(commonSurname, undefined),
      commonSurname,
      undefined,
      'farmer',
      factionId,
      '', // 平民没有家族
      { year: currentDate.year - age, month: randomInt(1, 12), day: randomInt(1, 28) },
      isMale ? 'male' : 'female'
    )
    characters.push(commoner)
  }

  return characters
}

// ============================================
// 辅助函数
// ============================================

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

function calculateAge(birthDate: GameDate, currentDate: GameDate): number {
  return currentDate.year - birthDate.year
}

function generateAbilities(_identity: CharacterIdentity, isRuler: boolean): CharacterAbilities {
  const base = isRuler ? 60 : 40

  return {
    intelligence: randomInt(base, base + 40),
    charisma: randomInt(base, base + 40),
    strength: randomInt(base - 10, base + 30),
    diplomacy: randomInt(base - 10, base + 40),
    administration: randomInt(base - 10, base + 40),
    military: randomInt(base - 20, base + 30),
    intrigue: randomInt(base - 20, base + 30),
    learning: randomInt(base - 10, base + 40),
    leadership: randomInt(base, base + 40),
    judgment: randomInt(base, base + 40),
    creativity: randomInt(base - 20, base + 40),
    resilience: randomInt(base, base + 40),
    loyalty: randomInt(30, 80),
    ambition: randomInt(20, 90),
  }
}

function generatePersonality(): CharacterPersonality {
  return {
    openness: randomInt(20, 90),
    conscientiousness: randomInt(20, 90),
    extraversion: randomInt(20, 90),
    agreeableness: randomInt(20, 90),
    neuroticism: randomInt(20, 90),
  }
}

function generateSchool(identity: CharacterIdentity): CharacterSchool {
  const schools: CharacterSchool[] = [
    'confucian',
    'legalist',
    'daoist',
    'mohist',
    'diplomat',
    'military',
    'yin_yang',
    'eclectic',
    'none',
  ]

  if (identity === 'scholar') return schools[randomInt(0, 4)]
  if (identity === 'general') return 'military'
  if (identity === 'official') return schools[randomInt(0, 3)]
  return 'none'
}

function generateAppearanceDescription(gender: 'male' | 'female', _age: number): string {
  const descriptions = gender === 'male'
    ? ['身材魁梧', '相貌堂堂', '仪表不凡', '英气勃发', '沉稳内敛']
    : ['容貌秀丽', '仪态端庄', '温婉贤淑', '明眸皓齿', '气质高雅']

  return descriptions[randomInt(0, descriptions.length - 1)]
}

function generateName(surname: string, clanName: string | undefined): string {
  const maleNames = ['文', '武', '成', '康', '景', '平', '桓', '庄', '襄', '昭', '惠', '宣', '懿', '孝', '仁', '义', '礼', '智', '信', '忠', '勇', '毅', '达', '明', '睿', '博', '厚', '高', '远', '大']
  const femaleNames = ['姬', '姜', '嬴', '芈', '姒', '妫', '子', '婉', '娴', '淑', '惠', '贤', '德', '庄', '宣', '文', '武', '成', '襄', '昭', '懿', '孝', '仁', '义', '礼', '智', '信']

  const isMale = Math.random() > 0.4
  const namePool = isMale ? maleNames : femaleNames
  const givenName = namePool[randomInt(0, namePool.length - 1)]

  if (clanName && clanName !== surname) {
    return `${surname}${clanName}${givenName}`
  }
  return `${surname}${givenName}`
}

function getRandomSurname(): string {
  const surnames = ['姬', '姜', '子', '嬴', '芈', '姒', '曹', '陈', '隗', '偃', '风', '姚', '妫', '云', '漆', '董', '杜', '范', '荀', '韩']
  return surnames[randomInt(0, surnames.length - 1)]
}

function getRandomClan(surname: string): string | undefined {
  const clans = FAMOUS_CLANS.filter(c => c.surname === surname)
  if (clans.length === 0) return undefined
  return clans[randomInt(0, clans.length - 1)]?.clanName
}

// ============================================
// 档案查询功能
// ============================================

/**
 * 查询家族档案
 */
export function queryFamilies(
  families: Family[],
  filters: {
    factionId?: string
    rank?: Family['rank']
    status?: Family['status']
    minPrestige?: number
  }
): Family[] {
  return families.filter(family => {
    if (filters.factionId && family.factionId !== filters.factionId) return false
    if (filters.rank && family.rank !== filters.rank) return false
    if (filters.status && family.status !== filters.status) return false
    if (filters.minPrestige && family.prestige < filters.minPrestige) return false
    return true
  })
}

/**
 * 查询人物档案
 */
export function queryCharacters(
  characters: CharacterArchive[],
  filters: {
    factionId?: string
    familyId?: string
    identity?: CharacterIdentity
    minAge?: number
    maxAge?: number
    gender?: 'male' | 'female'
    status?: string
  },
  currentDate: GameDate
): CharacterArchive[] {
  return characters.filter(char => {
    if (filters.factionId && char.factionId !== filters.factionId) return false
    if (filters.familyId && char.familyId !== filters.familyId) return false
    if (filters.identity && char.identity !== filters.identity) return false
    if (filters.gender && char.gender !== filters.gender) return false
    if (filters.status && char.status !== filters.status) return false

    const age = calculateAge(char.birthDate, currentDate)
    if (filters.minAge && age < filters.minAge) return false
    if (filters.maxAge && age > filters.maxAge) return false

    return true
  })
}

/**
 * 获取家族统计
 */
export function getFamilyStatistics(family: Family): {
  totalMembers: number
  maleCount: number
  femaleCount: number
  averageAge: number
  officialCount: number
} {
  return {
    totalMembers: family.memberIds.length,
    maleCount: 0, // 需要实际数据计算
    femaleCount: 0,
    averageAge: 0,
    officialCount: 0,
  }
}
